import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Stack,
  Fade,
  CircularProgress,
} from '@mui/material';
import { ArrowForward, LocalFlorist } from '@mui/icons-material';
import ProductCard from './ProductCard';
import { productAPI } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';

const FeaturedProducts = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { userCountry } = useCurrency();
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      setLoading(true);
      let params = {
        userCountry: userCountry
      }
      const response = await productAPI.getAllProducts(params);

      if (response.success && response.data) {
        // Transform backend data to match frontend structure
        const transformedProducts = response.data.map(product => {
          // Get the lowest price variant
          const getLowestPriceVariant = (variants) => {
            if (!variants || variants.length === 0) {
              return { sellingPrice: 0, mrp: 0 };
            }
            return variants.reduce((lowest, variant) => 
              variant.sellingPrice < lowest.sellingPrice ? variant : lowest
            , variants[0]);
          };

          const lowestVariant = getLowestPriceVariant(product.variants);

          return {
            id: product._id,
            name: product.name,
            category: product.subCategory?.name || 'Uncategorized',
            price: lowestVariant.sellingPrice,
            mrp: lowestVariant.mrp,
            image: product.images?.[0] || 'https://via.placeholder.com/500',
            description: product.description || '',
            benefits: product.attributes?.map(attr => attr.name) || [],
            featured: true,
            slug: product.slug,
            variants: product.variants || [],
            stock: lowestVariant.stock || 0,
          };
        });

        setFeaturedProducts(transformedProducts);
      }
    } catch (err) {
      console.error('Error fetching featured products:', err);
    } finally {
      setLoading(false);
    }
  };

  const goToShop = () => {
    navigate('/shop');
    window.scrollTo(0, 0);
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, #FFFFFF 0%, #F8F6F1 100%)',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Stack spacing={6}>
          {/* Section Header */}
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center' }}>
              <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 60,
                    height: 1,
                    bgcolor: 'primary.main',
                    opacity: 0.5,
                  }}
                />
                <LocalFlorist sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box
                  sx={{
                    width: 60,
                    height: 1,
                    bgcolor: 'primary.main',
                    opacity: 0.5,
                  }}
                />
              </Stack>

              <Typography
                variant="h2"
                className='font-fix'
                sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  color: 'text.primary',
                  mb: 2,
                }}
              >
                Featured Products
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 700,
                  mx: 'auto',
                  lineHeight: 1.8,
                  fontSize: { xs: '1rem', md: '1.15rem' },
                }}
              >
                Handpicked wellness essentials to begin your journey to holistic health
              </Typography>
            </Box>
          </Fade>

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={60} />
            </Box>
          )}

          {/* Featured Products Grid */}
          {!loading && featuredProducts.length > 0 && (
            <Fade in timeout={1000}>
              <Grid container spacing={4} className='prd-grid'>
                {featuredProducts.map((product, index) => (
                  <Grid item xs={12} sm={6} md={4} key={product.id} className='grid-cst'>
                    <Fade in timeout={1000 + index * 150}>
                      <Box>
                        <ProductCard product={product} />
                      </Box>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            </Fade>
          )}

          {/* Empty State */}
          {!loading && featuredProducts.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No products available at the moment
              </Typography>
            </Box>
          )}

          {/* View All Button */}
          <Fade in timeout={1400}>
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                className='see_all'
                size="large"
                endIcon={<ArrowForward />}
                onClick={goToShop}
                sx={{
                  borderRadius: 50,
                  px: 5,
                  py: 1.5,
                  borderWidth: 2,
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: 'primary.dark',
                    bgcolor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                View All Products
              </Button>
            </Box>
          </Fade>
        </Stack>
      </Container>
    </Box>
  );
};

export default FeaturedProducts;
